import React, { useEffect, useRef, useState } from "react";
import {
  Plus,
  Search,
  Users,
  TrendingUp,
  IndianRupee,
  Eye,
  Upload,
} from "lucide-react";
import vendor_profile_icon from "../../../assets/profile_icon.svg";
import { BASE_URL } from "utils/constant";
import AddVendor from "./AddVendor";
import ImportVendorModal from "./ImportVendorModal";
import VendorDetails from "./VendorDetails";
import OrganizationEmptyState from "components/UI_components/OrganizationEmptyState";
import VendorEmptyIcon from "assets/empty state/VendorEmpty.svg";
import RequireAccess from "components/RequireAccess";
// import Loader from "components/UI_components/Loader";
import LoadingScreen from "components/UI_components/LoadingScreen";

// Utility for classes
const cn = (...classes) => classes.filter(Boolean).join(" ");

export default function Vendor({ currentMember, permissionsLoaded }) {
  const [vendors, setVendors] = useState(
    JSON.parse(localStorage.getItem("vendors")) || [],
  );
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortField, setSortField] = useState("name");
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const sortMenuRef = useRef(null);

  const [showAddVendor, setShowAddVendor] = useState(false);
  const [showImportVendorModal, setShowImportVendorModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const organizationId = JSON.parse(localStorage.getItem("organization"))?.id;

  const fetchVendors = async () => {
    if (vendors.length === 0) {
      setLoading(true);
    }
    try {
      const token = localStorage.getItem("access");
      const org_id = JSON.parse(localStorage.getItem("organization"))?.id;

      const response = await fetch(
        `${BASE_URL}/inventory/list-org-vendors/${org_id}/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const result = await response.json();

      if (response.ok && result.status && Array.isArray(result.data)) {
        setVendors(result.data);
        localStorage.setItem("vendors", JSON.stringify(result.data));
      } else {
        console.error("Failed to load vendors", result.message);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchaseOrders = async () => {
    try {
      const token = localStorage.getItem("access");
      const org_id = JSON.parse(localStorage.getItem("organization"))?.id;

      const response = await fetch(
        `${BASE_URL}/inventory/purchase-orders/org/${org_id}/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setPurchaseOrders(data);
        }
      } else {
        console.error("Failed to load purchase orders");
      }
    } catch (error) {
      console.error("Error fetching purchase orders:", error);
    }
  };

  useEffect(() => {
    fetchVendors();
    fetchPurchaseOrders();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(event.target)) {
        setShowSortMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const totalVendors = vendors.length;
  const totalAdvance = vendors.reduce(
    (sum, v) => sum + (Number(v.advance_payment) || 0),
    0,
  );
  const totalPaid = vendors.reduce(
    (sum, v) => sum + (Number(v.paid_amount) || 0),
    0,
  );

  // Calculate Total Due: Sum of all line total_price - Sum of all paid_amount
  const totalDue = purchaseOrders.reduce((sum, po) => {
    const poLinesTotal =
      po.lines?.reduce(
        (lineSum, line) => lineSum + (Number(line.total_price) || 0),
        0,
      ) || 0;
    const poPaidAmount = Number(po.paid_amount) || 0;
    return sum + (poLinesTotal - poPaidAmount);
  }, 0);

  const formatCurrency = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)}L`;
    }
    return `₹${amount?.toLocaleString()}`;
  };

  const filteredVendors = [...vendors]
    .filter(
      (vendor) =>
        vendor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendor.vendor_type?.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => {
      const getValue = (vendor) => {
        if (sortField === "advance") {
          return Number(vendor.advance_payment) || 0;
        }
        if (sortField === "paid") {
          return Number(vendor.paid_amount) || 0;
        }
        return vendor.name || "";
      };

      const valueA = getValue(a);
      const valueB = getValue(b);

      if (sortField === "name") {
        return sortOrder === "asc"
          ? String(valueA).localeCompare(String(valueB))
          : String(valueB).localeCompare(String(valueA));
      }

      const diff = Number(valueA) - Number(valueB);
      return sortOrder === "asc" ? diff : -diff;
    });

  const paginatedVendors = filteredVendors.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const totalPages = Math.ceil(filteredVendors.length / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // UI Components
  const Button = ({
    variant = "default",
    size = "default",
    className = "",
    children,
    ...props
  }) => {
    const baseStyles =
      "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50";
    const variants = {
      default: "bg-sm-primary text-white shadow hover:bg-sm-primary/90",
      destructive:
        "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
      outline:
        "border border-input bg-transparent shadow-sm hover:bg-accent hover:text-sm-accent-foreground",
      secondary:
        "bg-sm-secondary text-sm-secondary-foreground shadow-sm hover:bg-sm-secondary/80",
      ghost: "hover:bg-accent hover:text-sm-accent-foreground",
      link: "text-sm-primary underline-offset-4 hover:underline",
    };
    const sizes = {
      default: "h-9 px-4 py-2",
      sm: "h-8 rounded-md px-3 text-xs",
      lg: "h-10 rounded-md px-8",
      icon: "h-9 w-9",
    };
    return (
      <button
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        {...props}
      >
        {children}
      </button>
    );
  };

  const Card = ({ className = "", children }) => (
    <div
      className={`rounded-lg border bg-white text-card-foreground shadow-sm ${className}`}
    >
      {children}
    </div>
  );

  const CardContent = ({ className = "", children }) => (
    <div className={`p-3 ${className}`}>{children}</div>
  );

  const Badge = ({ variant = "default", className = "", children }) => {
    const baseStyles =
      "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2";
    const variants = {
      default:
        "border-transparent bg-sm-primary text-white hover:bg-sm-primary/80",
      secondary: "border-transparent bg-sm-secondary/10 text-sm-primary",
      destructive: "border-transparent bg-red-100 text-red-900",
      outline: "text-foreground",
    };
    return (
      <div className={cn(baseStyles, variants[variant], className)}>
        {children}
      </div>
    );
  };

  if (!organizationId) {
    return <OrganizationEmptyState />;
  }

  if (
    currentMember?.vendor_control === false &&
    currentMember?.joined_as !== "owner" &&
    currentMember?.is_admin === false &&
    permissionsLoaded
  ) {
    return (
      <RequireAccess
        module="Vendor"
        currentMember={currentMember}
        permission="vendor_control"
      />
    );
  }

  if (selectedVendor) {
    return (
      <div className="w-[85%] h-[92vh] flex flex-col gap-3 p-3 overflow-y-scroll">
        <VendorDetails
          vendor={selectedVendor}
          onClose={() => setSelectedVendor(null)}
          onVendorDeleted={fetchVendors}
        />
      </div>
    );
  }

  return (
    <div className="w-full h-[90vh] flex-1 overflow-y-scroll">
      <LoadingScreen loading={loading}>
        <div className="w-full px-10 py-8 flex flex-col gap-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">
                Vendor Management
              </h1>
              <p className="text-sm text-muted-foreground">
                Manage vendors, procurement, and payments
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => setShowImportVendorModal(true)}
                className="h-9 text-sm gap-2 border-border/60"
              >
                <Upload className="h-4 w-4" />
                Import From Excel
              </Button>
              <Button
                onClick={() => setShowAddVendor(true)}
                className="bg-sm-primary hover:bg-sm-primary/90 text-white h-9 text-sm gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Vendor
              </Button>
            </div>
          </div>

          {/* Analytics Cards */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="border-border/60 shadow-none">
              <CardContent className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-sm-secondary/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-sm-secondary" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">
                    {totalVendors}
                  </p>
                  <p className="text-xs text-muted-foreground">Total Vendors</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/60 shadow-none">
              <CardContent className="p-2 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-sm-primary/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-sm-primary" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">
                    {formatCurrency(totalAdvance)}
                  </p>
                  <p className="text-xs text-muted-foreground">Total Advance</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/60 shadow-none">
              <CardContent className="p-2 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <IndianRupee className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">
                    {formatCurrency(totalPaid)}
                  </p>
                  <p className="text-xs text-muted-foreground">Total Paid</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/60 shadow-none">
              <CardContent className="p-2 flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                  <IndianRupee className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-2xl font-semibold text-foreground">
                    {formatCurrency(totalDue)}
                  </p>
                  <p className="text-xs text-muted-foreground">Total Due</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters Row */}
          <div className="flex items-center justify-between gap-4">
            <span className="text-sm text-muted-foreground">
              {filteredVendors.length} vendors
            </span>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  placeholder="Search vendors..."
                  className="h-9 text-sm pl-8 rounded-lg w-52 border border-black/60 focus:outline-none focus:ring-1 focus:ring-sm-primary"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="relative" ref={sortMenuRef}>
                <Button
                  variant="outline"
                  onClick={() => setShowSortMenu((prev) => !prev)}
                  className="h-9 text-sm gap-2 border-border/60"
                >
                  Sort By
                </Button>
                {showSortMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-20 overflow-hidden">
                    <button
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${sortField === "name" && sortOrder === "asc" ? "bg-sm-secondary/10 text-sm-primary font-medium" : ""}`}
                      onClick={() => {
                        setSortField("name");
                        setSortOrder("asc");
                        setCurrentPage(1);
                        setShowSortMenu(false);
                      }}
                    >
                      Name (A → Z)
                    </button>
                    <button
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${sortField === "name" && sortOrder === "desc" ? "bg-sm-secondary/10 text-sm-primary font-medium" : ""}`}
                      onClick={() => {
                        setSortField("name");
                        setSortOrder("desc");
                        setCurrentPage(1);
                        setShowSortMenu(false);
                      }}
                    >
                      Name (Z → A)
                    </button>
                    <div className="border-t border-gray-100" />
                    <button
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${sortField === "advance" && sortOrder === "desc" ? "bg-sm-secondary/10 text-sm-primary font-medium" : ""}`}
                      onClick={() => {
                        setSortField("advance");
                        setSortOrder("desc");
                        setCurrentPage(1);
                        setShowSortMenu(false);
                      }}
                    >
                      Advance (High → Low)
                    </button>
                    <button
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${sortField === "advance" && sortOrder === "asc" ? "bg-sm-secondary/10 text-sm-primary font-medium" : ""}`}
                      onClick={() => {
                        setSortField("advance");
                        setSortOrder("asc");
                        setCurrentPage(1);
                        setShowSortMenu(false);
                      }}
                    >
                      Advance (Low → High)
                    </button>
                    <div className="border-t border-gray-100" />
                    <button
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${sortField === "paid" && sortOrder === "desc" ? "bg-sm-secondary/10 text-sm-primary font-medium" : ""}`}
                      onClick={() => {
                        setSortField("paid");
                        setSortOrder("desc");
                        setCurrentPage(1);
                        setShowSortMenu(false);
                      }}
                    >
                      Paid (High → Low)
                    </button>
                    <button
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${sortField === "paid" && sortOrder === "asc" ? "bg-sm-secondary/10 text-sm-primary font-medium" : ""}`}
                      onClick={() => {
                        setSortField("paid");
                        setSortOrder("asc");
                        setCurrentPage(1);
                        setShowSortMenu(false);
                      }}
                    >
                      Paid (Low → High)
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Vendor Table */}
          <Card className="border-border/60 shadow-none flex-1 overflow-auto">
            <CardContent className="p-0">
              {paginatedVendors.length > 0 ? (
                <table className="w-full caption-bottom text-sm ring-offset-background">
                  <thead className="[&_tr]:border-b sticky top-0 bg-white z-10">
                    <tr className="border-b transition-colors hover:bg-transparent border-border/60">
                      <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground text-xs">
                        Vendor Details
                      </th>
                      <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground text-xs">
                        Type
                      </th>
                      <th className="h-10 px-4 align-middle font-medium text-muted-foreground text-xs text-right">
                        Advance Amount
                      </th>
                      <th className="h-10 px-4 align-middle font-medium text-muted-foreground text-xs text-right">
                        Total Paid
                      </th>
                      <th className="h-10 px-4 w-[14%] align-middle font-medium text-muted-foreground text-xs text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="[&_tr:last-child]:border-0 overflow-y-auto">
                    {paginatedVendors.map((vendor, index) => (
                      <tr
                        key={vendor.id || index}
                        className="group cursor-pointer hover:bg-muted/30 border-b border-border/40 transition-colors"
                        onClick={() => setSelectedVendor(vendor)}
                      >
                        <td className="p-4 align-middle py-3">
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-lg bg-sm-secondary/10 flex items-center justify-center overflow-hidden">
                              <img
                                src={vendor_profile_icon}
                                alt="Vendor"
                                className="w-5 h-5 object-contain"
                              />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground">
                                {vendor.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {vendor.address || "N/A"}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 align-middle">
                          <Badge
                            variant="secondary"
                            className="text-xs font-normal border-0"
                          >
                            {vendor.vendor_type}
                          </Badge>
                        </td>
                        <td className="p-4 align-middle text-right">
                          <span className="text-sm font-medium text-sm-primary">
                            ₹ {vendor.advance_payment?.toLocaleString() || "0"}
                          </span>
                        </td>
                        <td className="p-4 align-middle text-right">
                          <span className="text-sm font-medium text-green-600">
                            ₹ {vendor.paid_amount?.toLocaleString() || "0"}
                          </span>
                        </td>
                        <td className="align-middle text-center">
                          <button
                            className="px-2 py-2 items-center text-xs gap-1.5 hidden group-hover:inline-flex flex-row rounded-lg transition-opacity hover:bg-sm-accent hover:text-white"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedVendor(vendor);
                            }}
                          >
                            <Eye className="h-3.5 w-3.5" />
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="flex flex-col items-center justify-center py-20">
                  <img
                    src={VendorEmptyIcon}
                    alt="No Vendors"
                    className="mb-4"
                    width={200}
                  />
                  <span className="text-muted-foreground text-lg">
                    No Vendors Found
                  </span>
                  <p className="text-muted-foreground text-sm">
                    Add a vendor to start managing procurement.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pagination Controls */}
          {filteredVendors.length > 0 && (
            <div className="flex justify-between items-center mt-2 px-1">
              <span className="text-xs text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-muted-foreground">
                    Rows per page:
                  </label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="text-xs border border-border/60 rounded px-1 py-0.5 focus:outline-none"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="h-8 w-20"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="h-8 w-20"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}

          {showAddVendor && (
            <AddVendor
              onClose={() => {
                setShowAddVendor(false);
                fetchVendors();
              }}
            />
          )}

          {showImportVendorModal && (
            <ImportVendorModal
              organizationId={organizationId}
              onUploadSuccess={fetchVendors}
              onClose={() => setShowImportVendorModal(false)}
            />
          )}
        </div>
      </LoadingScreen>
    </div>
  );
}